import { useMemo } from "react";
import { JoinTelegram } from "@/components/site/JoinTelegram";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ExternalLink, Info, AlertTriangle, Tag, Scale, Link2 } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import { api, goUrl, inr, fmtDate, shareUrl } from "@/lib/api";
import { copyCode } from "@/lib/clipboard";
import { useSite } from "@/lib/site";

const SiteName = () => useSite().name;
import { useSeo } from "@/hooks/useSeo";
import { dealLd } from "@/lib/structuredData";
import { SaveButton } from "@/components/site/SaveButton";
import { PriceRow, DemoBadge, DealGrid, PriceDropBadge } from "@/components/site/DealCard";
import { Crumbs } from "@/components/site/bits";
import { useCompare } from "@/components/site/compare";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const LABELS = { discount: "Discount vs MRP", price_history: "Price vs our history", merchant: "Store reliability", freshness: "Price freshness", availability: "In stock & valid", popularity: "Shopper interest", content: "Listing quality" };
const MAX = { discount: 30, price_history: 15, merchant: 15, freshness: 15, availability: 10, popularity: 10, content: 5 };

const ScoreBox = ({ d }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="deal-score-box">
    <div className="flex items-center justify-between mb-4">
      <p className="font-bold"><SiteName /> score</p>
      <span className="font-display font-extrabold text-3xl text-emerald-700" data-testid="deal-score-value">{d.score}<span className="text-sm text-slate-400">/100</span></span>
    </div>
    <div className="space-y-2">
      {Object.entries(d.score_breakdown || {}).map(([k, v]) => (
        <div key={k} className="text-xs">
          <div className="flex justify-between text-slate-600"><span>{LABELS[k] || k}</span><span>{v}/{MAX[k]}</span></div>
          <div className="h-1.5 bg-slate-100 rounded-full mt-1"><div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(v / (MAX[k] || 1)) * 100}%` }} /></div>
        </div>
      ))}
    </div>
    {d.score_notes?.length > 0 && <p className="text-xs text-slate-500 mt-3">Note: {d.score_notes.join("; ")}.</p>}
    <p className="text-[11px] text-slate-400 mt-3">Commission earned by us is never part of this score.</p>
  </div>
);

const History = ({ points }) =>
  points?.length > 1 ? (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="price-history-chart">
      <p className="font-bold mb-3">Price history we recorded</p>
      <div className="h-40">
        <ResponsiveContainer>
          <LineChart data={points.map((p) => ({ date: fmtDate(p.at), price: p.price }))}>
            <XAxis dataKey="date" fontSize={10} /><YAxis fontSize={10} width={50} domain={[(min) => Math.floor(min * 0.9), (max) => Math.ceil(max * 1.05)]} />
            <Tooltip formatter={(v) => inr(v)} />
            <Line type="linear" dataKey="price" stroke="#059669" strokeWidth={2} dot connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  ) : null;

export default function DealPage() {
  const { slug } = useParams();
  const { toggle, has } = useCompare();
  const { data, isLoading, isError } = useQuery({ queryKey: ["deal", slug], queryFn: () => api.get(`/public/deals/${slug}`).then((r) => r.data) });
  const d = data?.deal;
  const jsonLd = useMemo(() => dealLd(d), [d]);
  useSeo({ title: d?.seo_title || d?.title, description: d?.meta_description || (d ? `${d.title} at ${inr(d.price)} on ${d.merchant_name}` : ""), image: d?.image_url, noindex: !d?.indexable, jsonLd });
  if (isLoading) return <div className="max-w-7xl mx-auto p-10"><div className="h-96 rounded-xl bg-slate-200 animate-pulse" /></div>;
  if (isError) return <div className="max-w-7xl mx-auto p-10 text-center" data-testid="deal-not-found">This deal is no longer available. <Link className="text-emerald-700 font-bold" to="/deals">See current deals</Link></div>;
  const expired = d.status === "expired";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="deal-page">
      <Crumbs items={[["/", "Home"], ["/deals", "Deals"], [`/category/${data.category?.slug}`, d.category_name], [null, d.title]]} />
      <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10">
        <div className="space-y-6">
          <div className="relative rounded-2xl overflow-hidden bg-white border border-slate-200">
            <img src={d.image_url} alt={d.alt_text || d.title} className="w-full aspect-[4/3] object-cover" />
            <span className="absolute top-4 left-4"><DemoBadge show={d.is_demo} /></span>
          </div>
          <div className="hidden lg:block"><History points={d.price_history} /></div>
        </div>
        <div className="space-y-6">
          {expired && <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 flex gap-2 text-sm" data-testid="deal-expired-banner"><AlertTriangle size={18} /> This offer has expired. Prices shown are the last ones we recorded.</div>}
          <div>
            <Link to={`/store/${data.merchant?.slug}`} className="text-sm font-bold text-emerald-700">{d.merchant_name}</Link>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-1" data-testid="deal-title">{d.title}</h1>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3" data-testid="deal-price-box">
            <PriceRow price={d.price} mrp={d.mrp} discount={d.discount_pct} size="lg" />
            {!expired && (d.lowest_recorded || d.price_drop_pct > 0) && (
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="deal-page-price-drop">
                <PriceDropBadge deal={d} />
                {d.previous_price && <span className="text-slate-600">Was {inr(d.previous_price)} when we last checked.</span>}
                {d.lowest_recorded && <span className="text-xs text-slate-500">Compared with {d.price_history?.length} prices we recorded - not the whole market.</span>}
              </div>
            )}
            {d.mrp > d.price && <p className="text-sm text-slate-600">You save <b>{inr(d.mrp - d.price)}</b> vs listed MRP. Last checked {fmtDate(d.price_updated_at)}.</p>}
            {d.coupon_code && <p className="text-sm flex items-center gap-2"><Tag size={14} /> Use code <span className="font-mono font-bold bg-amber-50 border border-dashed border-amber-400 px-2 rounded">{d.coupon_code}</span> at checkout</p>}
            {!expired && (
              <a href={goUrl(d.id, "deal_page")} target="_blank" rel="sponsored nofollow noopener" data-testid="deal-page-get-deal-button"
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold py-4 rounded-xl flex items-center justify-center gap-2 text-lg transition-colors">
                Get this deal on {d.merchant_name} <ExternalLink size={18} />
              </a>
            )}
            <button onClick={() => toggle(d.id)} data-testid="deal-page-compare-button" className="w-full border border-slate-200 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-50">
              <Scale size={16} /> {has(d.id) ? "Remove from compare" : "Add to compare"}
            </button>
            <SaveButton deal={d} label />
            <button onClick={() => copyCode(shareUrl(d.id), "Share link copied - paste it in WhatsApp or anywhere")} data-testid="deal-page-copy-link-button"
              className="w-full border border-slate-200 rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
              <Link2 size={16} /> Copy link
            </button>
            <JoinTelegram variant="compact" />
            <p className="text-xs text-slate-500 flex gap-1.5"><Info size={14} className="shrink-0 mt-0.5" /> Affiliate link - we may earn a commission at no extra cost to you. Final price, stock and delivery are decided by the store.</p>
          </div>
          <ScoreBox d={d} />
          <div className="lg:hidden"><History points={d.price_history} /></div>
        </div>
      </div>
      <section className="mt-12 grid lg:grid-cols-[1.1fr_1fr] gap-10">
        <div>
          <h2 className="text-2xl font-bold mb-3">About this deal</h2>
          {d.description ? <p className="text-slate-700 leading-relaxed whitespace-pre-line" data-testid="deal-description">{d.description}</p>
            : <p className="text-slate-500" data-testid="deal-description-missing">We haven't written a detailed review for this product yet. Please check full specifications on {d.merchant_name}.</p>}
        </div>
        {d.faqs?.length > 0 && (
          <div>
            <h2 className="text-2xl font-bold mb-3">FAQs</h2>
            <Accordion type="single" collapsible data-testid="deal-faqs">
              {d.faqs.map((f, i) => <AccordionItem key={i} value={`f${i}`}><AccordionTrigger className="text-left">{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>)}
            </Accordion>
          </div>
        )}
      </section>
      {data.related.length > 0 && <section className="mt-16"><h2 className="text-2xl font-bold mb-6">Similar deals</h2><DealGrid deals={data.related} testid="related-deals-grid" /></section>}
    </div>
  );
}
